import { usePasteImage } from "@/composables/usePasteImage";
import { MAX_FILE_SIZE_TEXTS } from "@/consts";
import { FileAcceptTypes, FileSizes } from "@/enums";
import helpers from "@/helpers";
import getFileCategory from "@/helpers/files/getFileCategory";
import type { IFileState } from "@/interfaces";
import type { FileCategory } from "@/types";
import { nanoid } from "nanoid";
import { ref, type Ref } from "vue";
import { useI18n } from "vue-i18n";

type FileUploaderOptions = {
  modelValue: Ref<IFileState[] | undefined>;
  accept: FileAcceptTypes;
  maxSize: FileSizes;
  autoReplace?: boolean;
  maxFile: number;
  favorite?: boolean;
  allowPasteImage?: boolean;
  onUpdate: (files: IFileState[] | undefined) => void;
};

export default function useFileUploads({
  modelValue,
  accept,
  maxSize,
  autoReplace,
  maxFile,
  favorite,
  allowPasteImage,
  onUpdate,
}: FileUploaderOptions) {
  const selectionError = ref<string>();
  const showPixabayDialog = ref(false);
  const showPictogramDialog = ref(false);

  const { t } = useI18n();

  const isTypesValid = (type: FileCategory, files: FileCategory[]) => {
    const matched = files.filter((item) => item === type);
    return matched.length === files.length;
  };

  const isFilesAcceptable = (types: FileCategory[]): boolean => {
    if (accept === FileAcceptTypes.OnlyImage && !isTypesValid("image", types)) {
      return false;
    }

    if (accept === FileAcceptTypes.OnlyPDF && !isTypesValid("pdf", types)) {
      return false;
    }

    if (accept === FileAcceptTypes.OnlyDocument && types.includes("image")) {
      return false;
    }

    return true;
  };

  const convertFiles = async (
    files: FileList | null,
  ): Promise<IFileState[]> => {
    if (!files) {
      return [];
    }

    const items: IFileState[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (!file) {
        continue;
      }

      items.push({
        id: nanoid(),
        name: file.name,
        originalName: file.name,
        size: file.size,
        base64: await helpers.files.toBase64(file),
        type: getFileCategory(file),
        action: "upload",
        file: file,
      });
    }

    return items;
  };

  const updateTheModel = (newFiles: IFileState[]) => {
    let totalCount = [...(modelValue.value || []), ...newFiles].filter(
      (item) => item.action !== "delete",
    ).length;

    // If the auto replace is enable, we should check for only new files
    if (autoReplace) {
      totalCount = newFiles.length;
    }

    // Check total file count
    if (totalCount > maxFile) {
      selectionError.value = t("global.file_count_error", {
        count: maxFile,
      });
      return;
    }

    // There can be only 1 favorite file if favorite feauture is enabled.
    if (favorite) {
      const hasFavorite = (modelValue.value || []).some(
        (item) => item.action !== "delete" && item.isFavorite,
      );
      if (hasFavorite === false && newFiles.length && newFiles[0]) {
        newFiles[0].isFavorite = true;
      }
    }

    // You can set the update the state completely.
    if (autoReplace) {
      if (favorite && newFiles[0]) {
        newFiles[0].isFavorite = true;
      }
      onUpdate(newFiles);
    } else {
      // Update the state
      const newState: IFileState[] = [...(modelValue.value || []), ...newFiles];
      onUpdate(newState);
    }
  };

  const onFileSelection = async (fileList: FileList | null) => {
    // Let's clear the errors
    selectionError.value = undefined;

    if (!fileList) {
      return;
    }

    // Converting array and create a new state
    const files: File[] = Array.from(fileList);
    // Check the maximum file size
    const unallowedSize = files.some((file) => file.size > maxSize);
    if (unallowedSize) {
      selectionError.value = t("global.file_size_error", {
        size: MAX_FILE_SIZE_TEXTS[maxSize || FileSizes.Max15MB],
      });
      return;
    }

    const fileTypes = files.map(getFileCategory);
    if (isFilesAcceptable(fileTypes) === false) {
      selectionError.value = t("global.file_type_error");
      return;
    }

    const newFiles = await convertFiles(fileList);

    updateTheModel(newFiles);
  };

  const onFileInputClick = () => {
    const input = document.createElement("input");
    input.type = "file";

    if (accept) {
      input.accept = accept;
    }

    input.multiple = maxFile > 1;

    input.onchange = async function () {
      onFileSelection(input.files);
    };
    input.click();
  };

  const handleDelete = (file: IFileState) => {
    if (file.action === "upload") {
      const newState = (modelValue.value || []).filter(
        (item) =>
          (file.base64 && item.base64 !== file.base64) ||
          (file.url && item.url !== file.url),
      );
      onUpdate(newState);
    } else {
      // This file is marked to delete
      file.action = "delete";

      // If the file is the favorite file, we should mark the next item as the
      // favorite file, if setting favorite is allowed, and there is a file.
      if (favorite && file.isFavorite) {
        const first = (modelValue.value || [])
          .filter((item) => item.action !== "delete")
          .at(0);
        if (first) {
          first.isFavorite = true;
        }
      }
    }
  };

  const handlePixabaySelection = async (files: IFileState[]) => {
    showPixabayDialog.value = false;

    // Let's clear the errors
    selectionError.value = undefined;

    if (!files.length) {
      return;
    }

    const newFiles: IFileState[] = files.map((file) => {
      return {
        id: file.id?.toString() || nanoid(),
        name: file.id?.toString() || "",
        originalName: file.id?.toString(),
        size: 0,
        base64: file.base64 || "",
        url: file.url || "",
        type: "image",
        action: "upload",
      };
    });

    updateTheModel(newFiles);
  };

  const handlePictogramSelection = async (files: IFileState[]) => {
    showPictogramDialog.value = false;

    // Let's clear the errors
    selectionError.value = undefined;

    if (!files.length) {
      return;
    }

    const newFiles: IFileState[] = files.map((file) => {
      return {
        id: file.id?.toString() || nanoid(),
        name: file.id?.toString() || "",
        originalName: file.id?.toString(),
        size: file.size,
        base64: file.base64 || "",
        url: file.url || "",
        type: "image",
        action: "upload",
        externalSource: "pictogram",
      };
    });

    updateTheModel(newFiles);
  };

  usePasteImage({
    enabled: allowPasteImage,
    onPaste: onFileSelection,
  });

  return {
    selectionError,
    showPixabayDialog,
    showPictogramDialog,
    onFileInputClick,
    onFileSelection,
    handleDelete,
    handlePixabaySelection,
    handlePictogramSelection,
  };
}
